import { SourcesTooltip } from '@src/entities/Source/ui/components/tooltips/Sources';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty, isUndefined } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * Table: Assignments
 *
 * Displays a list of action paths a risk record is assigned to.
 *
 * @param record
 * @returns {Element}
 * @constructor
 */
export function Assignments({ record = {} }) {
  /**
   * renderCellActionPath
   *
   * Displays the name of the action path within a cell. View action path if active otherwise edit on click.
   *
   * @param row
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellActionPath = ({ cell, row }) => {
    return (
      <Link
        href={`/action-paths/${row?.original?.actionPathId}${
          String(row?.original?.status).toLowerCase() !== 'active' ? '/edit' : ''
        }`}
      >
        {cell?.value}
      </Link>
    );
  };

  /**
   * renderCellLastUpdated
   *
   * Displays the date and time of when the action path was last updated within a cell.
   *
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellLastUpdated = ({ cell }) => {
    return dayjs(cell?.value).format('MM/DD/YYYY, HH:mm:ss');
  };

  /**
   * renderCellStatus
   *
   * Displays the current status of the action path within a cell.
   *
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellStatus = ({ cell }) => {
    const badges = {
      active: 'success',
      draft: 'neutral',
      inactive: 'error',
    };

    if (!isUndefined(badges[cell?.value.toLowerCase()])) {
      return (
        <Badge outline variant={badges[cell?.value.toLowerCase()]}>
          <span style={{ textTransform: 'capitalize' }}>{cell?.value.toLowerCase()}</span>
        </Badge>
      );
    }

    return cell.value;
  };

  /**
   * renderCellSourcesRemediated
   *
   * Displays a concatenated/truncated list of impacted sources within a cell.
   *
   * @param row
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellSourcesRemediated = ({ cell, row }) => {
    if (!isEmpty(row?.original?.recordActions)) {
      const sources = [];

      row?.original?.recordActions.forEach((recordAction) => {
        if (!isEmpty(recordAction?.recordReferences)) {
          recordAction?.recordReferences?.forEach((recordReference) => {
            if (!sources.includes(recordReference?.source)) {
              sources.push(recordReference?.source);
            }
          });
        }
      });

      return (
        <Tooltip content={<SourcesTooltip sources={sources} />} placement="auto">
          <span className="truncate">{sources.join(', ')}</span>
        </Tooltip>
      );
    }

    return cell.value;
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns.map((item) => {
      const column = item;

      if (column.Header === 'Action Path') {
        column.Cell = renderCellActionPath;
      }

      if (column.Header === 'Last Updated') {
        column.Cell = renderCellLastUpdated;
      }

      if (column.Header === 'Status') {
        column.Cell = renderCellStatus;
      }

      if (column.Header === 'Source(s) Remediated') {
        column.Cell = renderCellSourcesRemediated;
      }

      return column;
    });
  }, []);

  /**
   * Rows for table.
   */
  const rows = useMemo(() => {
    return record?.recordActionPaths || [];
  }, []);

  return (
    <ErrorHandler location="src/entities/ActionPath/ui/components/tables/Assignments/Assignments.jsx">
      <TableComponent
        {...{
          columns,
          configuration,
          dataKey: `actionPaths-RiskRecord-actionPaths-${record?.eid}`,
          rows,
        }}
      />
    </ErrorHandler>
  );
}
